/**
 * Form validation schemas using Zod
 */

import { z } from 'zod';
import { profileServicesSchema } from './profile-services';
import { isValidPhoneNumber } from './phone-format';
import { BUSINESS_CONFIG } from './constants';
import { validateTimeOrder } from './date-utils';

// ============================================================================
// Authentication Validation Schemas
// ============================================================================

/**
 * Password validation schema with strength requirements
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');

/**
 * Email validation schema
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

/**
 * Registration form validation schema
 */
export const registerSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * Login form validation schema
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

/**
 * Forgot Password form validation schema
 */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

// ============================================================================
// Email Verification Validation Schemas
// ============================================================================

/**
 * Resend verification email schema
 */
export const resendVerificationSchema = z.object({
  email: emailSchema,
});

// ============================================================================
// Onboarding Validation Schemas
// ============================================================================

/**
 * Personal info form validation schema
 */
const notificationPreferenceSchema = z.enum(['TEXT', 'EMAIL']);

const personalInfoBaseSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'First name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'Last name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine(isValidPhoneNumber, 'Please enter a valid 10-digit phone number'),
  timeZone: z.string().trim().min(1, 'Time zone is required'),
});

export const caregiverPersonalInfoSchema = personalInfoBaseSchema.extend({
  notificationPrefs: z
    .array(notificationPreferenceSchema)
    .min(1, 'Please allow notifications from your caregiver'),
});

export const supervisorPersonalInfoSchema = personalInfoBaseSchema;

/**
 * Services form validation schema
 */
export const servicesSchema = z
  .object({
    serviceTypes: profileServicesSchema.min(1, 'Please select at least one service type'),
    ageRangesNotApplicable: z.boolean(),
    ageRanges: z.array(z.enum(BUSINESS_CONFIG.AGE_RANGE_TYPES)),
    serviceAreas: z
      .string()
      .trim()
      .min(1, 'Please add your service area')
      .max(255, 'Service area must be 255 characters or less'),
    aboutMe: z
      .string()
      .trim()
      .min(1, 'Please tell us about yourself')
      .min(50, 'Please write at least 50 characters (about 3-5 sentences)')
      .max(
        BUSINESS_CONFIG.PROFILE.ABOUT_ME.MAX_LENGTH,
        `Please keep your description to ${BUSINESS_CONFIG.PROFILE.ABOUT_ME.MAX_LENGTH} characters or less`
      ),
  })
  .superRefine((data, ctx) => {
    if (!data.ageRangesNotApplicable && data.ageRanges.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select at least one age range',
        path: ['ageRanges'],
      });
    }
  });

// ============================================================================
// Password Strength Checker
// ============================================================================

export interface PasswordStrength {
  score: number; // 0-4
  label: 'Weak' | 'Fair' | 'Good' | 'Strong';
  color: string;
  suggestions: string[];
}

/**
 * Calculate password strength
 */
export function checkPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  const suggestions: string[] = [];

  // Length check
  if (password.length >= 8) score++;
  else suggestions.push('Use at least 8 characters');

  if (password.length >= 12) score++;

  // Character variety checks
  if (/[a-z]/.test(password)) score++;
  else suggestions.push('Add lowercase letters');

  if (/[A-Z]/.test(password)) score++;
  else suggestions.push('Add uppercase letters');

  if (/[0-9]/.test(password)) score++;
  else suggestions.push('Add numbers');

  if (/[^A-Za-z0-9]/.test(password)) score++;
  else suggestions.push('Add special characters');

  // Normalize score to 0-4
  const normalizedScore = Math.min(Math.floor(score / 1.5), 4);

  // Determine label and color
  let label: PasswordStrength['label'];
  let color: string;

  if (normalizedScore <= 1) {
    label = 'Weak';
    color = 'bg-error';
  } else if (normalizedScore === 2) {
    label = 'Fair';
    color = 'bg-orange-500';
  } else if (normalizedScore === 3) {
    label = 'Good';
    color = 'bg-warning';
  } else {
    label = 'Strong';
    color = 'bg-success-dark';
  }

  return {
    score: normalizedScore,
    label,
    color,
    suggestions: suggestions.slice(0, 2), // Limit to 2 suggestions
  };
}

// ============================================================================
// Availability Validation Schemas
// ============================================================================

/**
 * Availability slot form validation schema (without recurrence)
 */
export const availabilitySlotSchema = z
  .object({
    date: z.string().min(1, 'Date is required'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().min(1, 'End time is required'),
  })
  .refine(
    data => validateTimeOrder(data.startTime, data.endTime),
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    }
  );

/**
 * Availability slot form validation schema with recurrence support
 */
export const availabilitySlotWithRecurrenceSchema = z
  .object({
    date: z.string().min(1, 'Date is required'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().min(1, 'End time is required'),
    recurrence: z.enum(['NO_REPEAT', 'DAILY', 'WEEKLY']),
    endDate: z.string().optional().nullable(),
  })
  .refine(
    data => validateTimeOrder(data.startTime, data.endTime),
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    }
  )
  .refine(
    data => {
      // End date, if provided, must be after start date
      if (data.endDate && data.date) {
        return new Date(data.endDate) >= new Date(data.date);
      }
      return true;
    },
    {
      message: 'End date must be on or after start date',
      path: ['endDate'],
    }
  );

// ============================================================================
// Booking Validation Schemas
// ============================================================================

/**
 * Booking request form validation schema
 */
export const caregiverCalendarAccessRequestSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, 'Full name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  email: emailSchema,
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine(isValidPhoneNumber, 'Please enter a valid 10-digit phone number'),
  message: z
    .string()
    .trim()
    .min(1, 'Message is required')
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message must be less than 1000 characters'),
});

/**
 * Booking request form validation schema
 */
export const bookingRequestSchema = z.object({
  careClientName: z
    .string()
    .min(1, 'Full name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  careClientContactNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine(isValidPhoneNumber, 'Please enter a valid 10-digit phone number'),
  careClientEmail: z.string().trim().toLowerCase().email('Please enter a valid email address'),
  requestedStartTime: z.string().min(1, 'Start time is required'),
  requestedDuration: z
    .number()
    .min(15, 'Duration must be at least 15 minutes')
    .max(480, 'Duration must be less than 8 hours'),
  messageFromCareClient: z.string().max(500, 'Message must be less than 500 characters').optional(),
  careClientNotificationPrefs: z
    .array(z.enum(['TEXT', 'EMAIL']))
    .min(1, 'Please allow notifications from your caregiver'),
});

/**
 * Accept booking form validation schema
 */
export const acceptBookingSchema = z.object({
  messageToClient: z.string().max(500, 'Message must be less than 500 characters').optional(),
});

/**
 * Decline booking form validation schema
 */
export const declineBookingSchema = z.object({
  declinedReason: z
    .string()
    .min(10, 'Please provide a reason of at least 10 characters')
    .max(500, 'Reason must be less than 500 characters'),
});

/**
 * Cancel booking form validation schema
 */
export const cancelBookingSchema = z.object({
  cancelReasonMessage: z
    .string()
    .min(10, 'Please provide a reason of at least 10 characters')
    .max(500, 'Reason must be less than 500 characters'),
});

/**
 * Direct booking form validation schema
 */
export const directBookingSchema = z
  .object({
    date: z.string().min(1, 'Date is required'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().min(1, 'End time is required'),
    careClientContactNumber: z
      .string()
      .min(1, 'Phone number is required')
      .refine(isValidPhoneNumber, 'Please enter a valid 10-digit phone number'),
    careClientName: z
      .string()
      .min(1, 'Name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters')
      .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
    careClientEmail: emailSchema,
    notes: z.string().max(500, 'Notes must be less than 500 characters').optional(),
  })
  .refine(
    data => validateTimeOrder(data.startTime, data.endTime),
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    }
  )
  .refine(
    data => {
      if (data.startTime && data.endTime) {
        const [startHour, startMinute] = data.startTime.split(':').map(Number);
        const [endHour, endMinute] = data.endTime.split(':').map(Number);
        const duration = endHour * 60 + endMinute - (startHour * 60 + startMinute);
        return duration >= 15 && duration <= 480;
      }
      return true;
    },
    {
      message: 'Duration must be between 15 minutes and 8 hours',
      path: ['endTime'],
    }
  );

// ============================================================================
// Profile Management Validation Schemas
// ============================================================================

/**
 * Profile access toggle validation schema
 */
export const profileAccessSchema = z.object({
  isPublic: z.boolean(),
});

/**
 * Master availability toggle validation schema
 */
export const masterAvailabilitySchema = z.object({
  isAvailable: z.boolean(),
});

// ============================================================================
// Export types
// ============================================================================

export type RegisterFormData = z.infer<typeof registerSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type ResendVerificationFormData = z.infer<typeof resendVerificationSchema>;
export type CaregiverPersonalInfoFormData = z.infer<typeof caregiverPersonalInfoSchema>;
export type SupervisorPersonalInfoFormData = z.infer<typeof supervisorPersonalInfoSchema>;
export type ServicesFormData = z.infer<typeof servicesSchema>;
export type AvailabilitySlotFormData = z.infer<typeof availabilitySlotSchema>;
export type AvailabilitySlotWithRecurrenceFormData = z.infer<
  typeof availabilitySlotWithRecurrenceSchema
>;
export type BookingRequestFormData = z.infer<typeof bookingRequestSchema>;
export type CaregiverCalendarAccessRequestFormData = z.infer<
  typeof caregiverCalendarAccessRequestSchema
>;
export type AcceptBookingFormData = z.infer<typeof acceptBookingSchema>;
export type DeclineBookingFormData = z.infer<typeof declineBookingSchema>;
export type CancelBookingFormData = z.infer<typeof cancelBookingSchema>;
export type DirectBookingFormData = z.infer<typeof directBookingSchema>;
export type ProfileAccessFormData = z.infer<typeof profileAccessSchema>;
export type MasterAvailabilityFormData = z.infer<typeof masterAvailabilitySchema>;
